import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import useStores from '@lib/hooks/useStores';
import { Menu, Progress, Upload, message } from 'antd';
import { RcFile } from 'antd/lib/upload';
// import { LoadingOutlined } from '@ant-design/icons';

import { IUserStore } from '@stores/userStore';
import { scMixinEllipsis } from '@utils/emotion/mixins';
import { UploaderContext } from '~/lib/video-uploader';
import { ReactComponent as IconDelete } from '@svg/icon-delete.svg';
import { ReactComponent as IconUpload } from '@svg/icon-upload-basic.svg';

const UploadingVideosDropdown: React.FC = observer(() => {
  const { userStore } = useStores<{ userStore: IUserStore }>();
  const { t } = useTranslation();
  const videoUploader = useContext(UploaderContext);
  const location = useLocation();
  const navigate = useNavigate();

  const handleBeforeUpload = (file: RcFile) => {
    if (file.name.endsWith('.mkv') && file.type === '') return true;
    switch (file.type) {
      case 'video/mp4':
      case 'video/webm':
      case '.mkv':
      case 'video/quicktime':
      case 'video/x-msvideo':
      case 'video/x-flv':
        return true;
      default:
        message.error(t('upload.error.format'));
        return false;
    }
  };

  return (
    <DropdownContainer>
      <div className="title">
        <h2 className="text">{t('header.action.upload.desc')}</h2>
      </div>
      <Menu
        mode="inline"
        items={Object.keys(userStore.currentlyUploadingVideoDatas).map((uid) => {
          const { videoId, state, title, progress } = userStore.currentlyUploadingVideoDatas[uid];
          const isComplete = state === 'complete';
          const detailedStatus = state === 'uploading' && progress === 0 ? 'standby' : state;

          return {
            key: uid,
            label: (
              <VideoItemBlock>
                <Link
                  to={videoId ? `/video/${videoId}/edit` : '/'}
                  onClick={(e) => {
                    if (!videoId) e.preventDefault();
                    if (state === 'complete') videoUploader.cancelVideoUpload(uid); // Delete from list
                  }}
                  className="inner"
                  data-completed={isComplete ? 'true' : 'false'}
                >
                  <div className="indicator">
                    <div className="progress">
                      <Progress
                        type="circle"
                        percent={progress}
                        strokeColor={isComplete ? '#52c41a' : '#334d6e'}
                        width={48}
                        className="progress-bar"
                      />
                    </div>
                  </div>
                  <div className="info">
                    <div className="primary-info">
                      <strong className="title">{title}</strong>
                      <div className="status">
                        {t(`header.action.upload.status.${detailedStatus}`)}
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="action-area">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      videoUploader.cancelVideoUpload(uid);

                      if (location.pathname === `/video/${videoId}/edit`) {
                        window.setTimeout(() => {
                          navigate('/videos', { replace: true });
                        }, 1000);
                      }
                    }}
                    aria-label="Cancel Upload Video"
                  >
                    <i className="icon">
                      <IconDelete width="14" height="18" />
                    </i>
                  </button>
                </div>
              </VideoItemBlock>
            ),
          };
        })}
      />
      <div className="btn-upload">
        <Upload
          name="video"
          multiple
          showUploadList={false}
          beforeUpload={handleBeforeUpload}
          customRequest={(options) => videoUploader.createVideoUpload(options)}
        >
          <button>
            <i className="icon">
              <IconUpload />
            </i>
            {t('upload.title')}
          </button>
        </Upload>
      </div>
    </DropdownContainer>
  );
});

const DropdownContainer = styled.div`
  position: relative;
  top: 13px;
  width: 360px;
  padding: 8px 0 0 !important;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1) !important;
  background-color: #fcfcfc;

  & > .title {
    padding: 4px 16px 10px;

    .text {
      margin: 0;
      font-weight: bold;
      font-size: 12px;
      line-height: 14px;
    }
  }

  .btn-upload {
    & > span,
    .ant-upload,
    .ant-upload > span {
      display: block;
    }

    button {
      display: block;
      width: 100%;
      padding: 18px 9px;
      background-color: #5100ce;
      font-weight: bold;
      font-size: 14px;
      line-height: 19px;
      color: #f5f6f8;
      cursor: pointer;

      .icon {
        position: relative;
        top: 1px;
        margin-right: 8px;
        vertical-align: middle;
      }
    }
  }

  .ant-menu-item {
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;

    &:first-of-type > div {
      border-top: none !important;
    }

    & > a::before {
      pointer-events: none;
    }
  }
`;

const VideoItemBlock = styled.div`
  border-top: 1px solid #e9e9e9;

  .inner {
    display: flex;
    flex-direction: row;
    overflow: hidden;
    padding: 8px 16px;
    transition: background-color 200ms ease;
    color: #555;

    &:hover[data-completed='false'],
    &:focus[data-completed='false'] {
      background-color: #f9f9f9;

      .info {
        padding-right: 30px;
      }

      & + .action-area {
        opacity: 1;
        pointer-events: auto;
      }
    }
  }

  .indicator {
    position: relative;
    width: 50px;
    height: 56px;

    img {
      display: block;
      height: 100%;
    }

    .progress {
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 1;
      width: 48px;
      height: 48px;
      margin: -24px 0 0 -24px;
    }

    .ant-progress-text {
      color: #334d6e;

      span {
        margin: 0 auto;
      }
    }
  }

  .info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    padding-left: 16px;

    .sub-info {
      margin-top: 8px;
    }

    .title {
      ${scMixinEllipsis};
      display: block;
      line-height: 1.25;
    }

    .status {
      margin-top: 4px;
      font-size: 11px;
      line-height: 1.2;
      color: #999;
    }
  }

  .action-area {
    display: flex;
    align-items: center;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    padding: 0 16px;
    opacity: 0;
    pointer-events: none;
    transition: all 200ms ease;

    &:hover {
      transform: scale(1.05);
      opacity: 1;
      pointer-events: auto;
    }

    button {
      cursor: pointer;
    }
  }
`;

export default UploadingVideosDropdown;
